import React from 'react';
import { ValidCell } from '../types';

interface SVGBuildingCellProps {
    buildingId: string;
    baseScore: number;
    controlRatio: {
        teamA: number;    // 0-1 (percentage of adjacent cells)
        teamB: number;
        competitor: number;
    };
    isHovered?: boolean;
}

// Map scores to SVG files
const getSVGFile = (score: number): string => {
    if (score === 60) return '/Building-1.svg';
    if (score === 48) return '/Building-2.svg';
    if (score === 24) return '/Building-3.svg';
    return '/Building-4.svg'; // 12 pts
};

// Get size based on score
const getBuildingSize = (score: number): number => {
    if (score === 60) return 70;
    if (score === 48) return 60;
    if (score === 24) return 50;
    return 40;
};

// Calculate dominant color based on control
const getDominantColor = (control: { teamA: number; teamB: number; competitor: number }): string => {
    const { teamA, teamB, competitor } = control;

    // Competitor takes priority if significant
    if (competitor > 0.5) {
        return `rgba(245, 158, 11, ${0.3 + competitor * 0.7})`; // Orange
    }

    // Team A dominates
    if (teamA > teamB && teamA > competitor) {
        return `rgba(59, 130, 246, ${0.2 + teamA * 0.8})`; // Blue
    }

    // Team B dominates
    if (teamB > teamA && teamB > competitor) {
        return `rgba(239, 68, 68, ${0.2 + teamB * 0.8})`; // Red
    }

    // Mixed/contested - purple
    if (teamA > 0 && teamB > 0) {
        return `rgba(147, 51, 234, 0.5)`; // Purple
    }

    // No control - neutral gray
    return 'rgba(100, 116, 139, 0.3)';
};

// Get glow effect for hover/selection
const getGlowColor = (control: { teamA: number; teamB: number; competitor: number }): string => {
    const { teamA, teamB, competitor } = control;

    if (competitor > 0.5) return 'rgba(245, 158, 11, 0.6)';
    if (teamA > teamB) return 'rgba(59, 130, 246, 0.6)';
    if (teamB > teamA) return 'rgba(239, 68, 68, 0.6)';
    return 'rgba(147, 51, 234, 0.6)';
};

const SVGBuildingCell: React.FC<SVGBuildingCellProps> = ({
    buildingId,
    baseScore,
    controlRatio,
    isHovered = false
}) => {
    const size = getBuildingSize(baseScore);
    const svgFile = getSVGFile(baseScore);
    const dominantColor = getDominantColor(controlRatio);
    const glowColor = getGlowColor(controlRatio);

    // Calculate dynamic score with bonus
    const totalControl = controlRatio.teamA + controlRatio.teamB + controlRatio.competitor;
    const teamCount = [controlRatio.teamA, controlRatio.teamB, controlRatio.competitor]
        .filter(c => c > 0).length;

    let bonus = 0;
    if (teamCount === 1) bonus = 6;
    else if (teamCount === 2) bonus = 3;

    const dynamicScore = baseScore + bonus;

    return (
        <div
            style={{
                width: `${size}px`,
                height: `${size}px`,
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.3s ease'
            }}
        >
            {/* SVG Container with color overlay */}
            <div
                style={{
                    width: '100%',
                    height: '100%',
                    position: 'relative',
                    background: dominantColor,
                    borderRadius: 'var(--radius-md)',
                    boxShadow: isHovered
                        ? `0 0 20px ${glowColor}, 0 4px 12px rgba(0,0,0,0.3)`
                        : '0 2px 8px rgba(0,0,0,0.2)',
                    transform: isHovered ? 'scale(1.05)' : 'scale(1)',
                    transition: 'all 0.3s ease',
                    overflow: 'hidden'
                }}
            >
                {/* SVG Image */}
                <img
                    src={svgFile}
                    alt={buildingId}
                    style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'contain',
                        opacity: 0.9,
                        mixBlendMode: 'overlay',
                        filter: 'brightness(1.2)'
                    }}
                />

                {/* Score Badge */}
                <div
                    style={{
                        position: 'absolute',
                        bottom: '4px',
                        right: '4px',
                        background: 'rgba(0, 0, 0, 0.8)',
                        color: 'white',
                        padding: '2px 6px',
                        borderRadius: 'var(--radius-sm)',
                        fontSize: '0.7rem',
                        fontWeight: '700',
                        border: '1px solid rgba(255, 255, 255, 0.2)'
                    }}
                >
                    {dynamicScore}
                </div>

                {/* Control Indicator Bars */}
                {totalControl > 0 && (
                    <div
                        style={{
                            position: 'absolute',
                            bottom: 0,
                            left: 0,
                            right: 0,
                            height: '4px',
                            display: 'flex',
                            gap: '1px',
                            background: 'rgba(0, 0, 0, 0.3)'
                        }}
                    >
                        {controlRatio.teamA > 0 && (
                            <div
                                style={{
                                    flex: controlRatio.teamA,
                                    background: 'var(--team-a)',
                                    height: '100%'
                                }}
                            />
                        )}
                        {controlRatio.teamB > 0 && (
                            <div
                                style={{
                                    flex: controlRatio.teamB,
                                    background: 'var(--team-b)',
                                    height: '100%'
                                }}
                            />
                        )}
                        {controlRatio.competitor > 0 && (
                            <div
                                style={{
                                    flex: controlRatio.competitor,
                                    background: 'var(--competitor)',
                                    height: '100%'
                                }}
                            />
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default SVGBuildingCell;
